// Session Service - PCI Compliant Authentication Management
import securityService from './securityService';
import secureCookieService from './secureCookieService.js';

class SessionService {
  constructor() {
    this.authToken = secureCookieService.getSessionToken();
    this.lambdaURL = 'https://api-frankfurt.rezcomm.com/central/auth/token';
  }

  // Generate token with brand_id for authenticated API calls
  async generateToken(brandId, userEmail = null, userUuid = null) {
    try {
      const payload = {
        action: 'generate',
        brandCode: brandId
      };
      
      // Use actual user data if provided, otherwise use synthetic values
      if (userEmail && userUuid) {
        payload.email = userEmail;
        payload.uuid = userUuid;
      } else {
        payload.email = 'system@' + brandId;
        payload.uuid = 'brand_' + brandId;
      }
      
      const response = await fetch(this.lambdaURL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) throw new Error('Token generation failed');
      
      const data = await response.json();
      this.authToken = data.token;
      
      // Only use secure cookies - no localStorage
      secureCookieService.setSessionToken(this.authToken);
      
      return data.token;
    } catch (error) {
      console.error('Token generation failed:', error);
      throw error;
    }
  }

  // Validate token with retry logic
  async validateToken() {
    if (!this.authToken) {
      throw new Error('No authentication token found');
    }

    try {
      return await securityService.validateSession(this.authToken);
    } catch (error) {
      console.error('Token validation failed:', error);
      this.clearToken();
      throw error;
    }
  }

  // Refresh token for PCI compliance
  async refreshToken() {
    try {
      const response = await fetch(this.lambdaURL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'refresh',
          token: this.authToken
        })
      });
      
      if (!response.ok) throw new Error('Token refresh failed');
      
      const data = await response.json();
      this.authToken = data.token;
      
      // Only use secure cookies - no localStorage
      secureCookieService.setSessionToken(this.authToken);
      
      return data;
    } catch (error) {
      console.error('Token refresh failed:', error);
      this.clearToken();
      throw error;
    }
  }

  // Get current token from secure cookie
  getToken() {
    this.authToken = secureCookieService.getSessionToken();
    return this.authToken;
  }

  // Secure token clearing
  clearToken() {
    securityService.secureLogout();
    this.authToken = null;
  }

  // Check authentication with session integrity
  isAuthenticated() {
    if (!this.authToken) return false;
    return securityService.validateSessionIntegrity(this.authToken);
  }



  // Generate secure token for session management
  generateSecureToken() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  // Create temporary token for pre-authentication APIs
  createTempToken() {
    return 'temp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }
}

export default new SessionService();